'use client'

import * as React from 'react'
import * as Popover from 'primitives/popover'
import { Button } from '@repo/design-system-cf/components/button'
import { DotsHorizontalIcon } from 'primitives/icons'
import {
  IconBookmark,
  IconBookmarkAdd,
  IconClose,
  IconShare,
  IconDelete,
  IconLink,
} from '@repo/design-system-cf/components/icons'
import { useIsLoggedInQuery } from 'auth/hooks'
import { useSocialShareDialog } from '@repo/web-ui/ask-cf/contexts/social-share-dialog-context'
import { NEXT_PUBLIC_CF_URL } from '@web/utils/env'
import { useMutation } from '@tanstack/react-query'
import { useUserAuthModal } from '@repo/user-auth-dialog/user-auth-dialog-context'
import { useBreakpoint } from '@repo/web-ui/hooks/use-breakpoints'
import toast from 'react-hot-toast'
import type { Project } from '../page-client'
import * as Dialog from 'primitives/dialog'
import { tw } from 'utils/classnames'
import { Z_INDEX_MODALS } from 'constants/modals'
import { useSocialPostSaveMutation, useSocialPostUnsaveMutation } from '@web/gen/feed-post'
import { useProject } from '../_hooks/use-project-data'
import { useTranslations } from 'next-intl'
import { Link } from '@repo/web-ui/components/link'
import { useDeleteDialog } from '../contexts/delete-dialog-context'
import { DeleteDialogMobile } from './dialogs/delete-post-comment-dialog'
import { useUser } from '@repo/cf-user/hooks/use-user-data'
import { useUserCapabilitiesQuery } from '@web/gen/user'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { usePathname } from 'next/navigation'
import { useFeedRoute } from '../contexts/feed-route-context'

export const ProjectMoreMenu = ({
  project,
  showViewPostLink = true,
  inPhotoViewMode = false,
}: {
  project: Project
  showViewPostLink?: boolean
  inPhotoViewMode?: boolean
}) => {
  const { openModal } = useUserAuthModal()
  const { breakpoint } = useBreakpoint()
  const { openDialog } = useSocialShareDialog()
  const user = useUser()
  const t = useTranslations('pages.masterfeed')
  const pathname = usePathname()

  const { basePath } = useFeedRoute()
  const isMobile = ['xs', 'sm'].includes(breakpoint ?? '')

  const [isOpen, setIsOpen] = React.useState(false)
  const isLoggedInQuery = useIsLoggedInQuery()
  const saveMutation = useSocialPostSaveMutation()
  const unsaveMutation = useSocialPostUnsaveMutation()
  const { refetch, project: projectData } = useProject({ postSlug: project?.postSlug || '' })

  const { openDialog: openDeleteDialog } = useDeleteDialog()
  const { data: userCapabilities } = useUserCapabilitiesQuery(undefined, {
    select: data => data?.me?.user,
  })

  const redirectTo = React.useMemo(() => {
    if (!pathname) {
      return basePath
    }
    const cleaned = pathname.replace(/\/+$/, '')
    const parts = cleaned.split('/').filter(Boolean)
    const photoIdx = parts.lastIndexOf('photo')

    if (photoIdx !== -1) {
      const base = '/' + parts.slice(0, photoIdx).join('/') + '/'
      return base
    }

    return basePath
  }, [pathname, basePath])

  const handleOpenDeleteDialog = () => {
    if (showViewPostLink && !inPhotoViewMode) {
      openDeleteDialog(project.id, 'post')
    } else if (inPhotoViewMode) {
      openDeleteDialog(project.id, 'post', {
        isPostPage: true,
        redirectTo: `${NEXT_PUBLIC_CF_URL}${redirectTo}`,
      })
    } else {
      openDeleteDialog(project.id, 'post', {
        isPostPage: true,
        redirectTo: basePath,
      })
    }
  }

  const { mutate: toggleBookmark } = useMutation({
    // oxlint-disable-next-line require-await
    mutationFn: async (currentlySaved: boolean) => {
      return currentlySaved
        ? unsaveMutation.mutateAsync({ input: { postId: project?.id } })
        : saveMutation.mutateAsync({ input: { postId: project?.id } })
    },
    onSuccess: (_, currentlySaved) => {
      if (!currentlySaved) {
        toast.success(
          <div className="flex text-14 gap-1.5 font-semibold">
            <p>Added to your </p>
            <a className="underline" href={`${basePath}/saved`}>
              saved posts
            </a>
          </div>,
          {
            duration: 5000,
          },
        )
      }
      refetch()
    },
  })

  const handleShareProject = () => {
    openDialog(`${NEXT_PUBLIC_CF_URL}${basePath}/${project.postSlug}/`, 'Share Project')
    setIsOpen(false)
  }

  const handleToggleBookmark = () => {
    if (!isLoggedInQuery.isPending && !isLoggedInQuery.data) {
      openModal('register')
      return
    }
    toggleBookmark(projectData?.isSaved ?? false)
    setIsOpen(false)
  }

  const postUrl = `${NEXT_PUBLIC_CF_URL}${basePath}/${project.postSlug}/`
  const isPostOwner = user.data?.id === project.user?.id
  const showDelete =
    !!userCapabilities?.capabilities.includes('dashboard_masterfeed') || isPostOwner

  const hideSubMenu = !showDelete && !showViewPostLink

  // in Mobile we open the modal instead of the popover
  if (isMobile) {
    // oxlint-disable-next-line no-negated-condition
    return !hideSubMenu ? (
      <>
        <Button
          variant={'ghost'}
          size="small"
          onClick={() => {
            setIsOpen(true)
          }}
          data-testid="project-more-menu-button"
          aria-label="Open project actions menu"
          iconLeft={<DotsHorizontalIcon className="text-cf-purple-900" />}
        />
        <ProjectMoreMenuModal
          project={projectData}
          isOpen={isOpen}
          closeModal={() => setIsOpen(false)}
          handleToggleBookmark={handleToggleBookmark}
          handleShareProject={handleShareProject}
          showViewPostLink={showViewPostLink}
          showDelteOption={(showDelete && isLoggedInQuery.data) || false}
        />
      </>
    ) : null
  }

  // in Desktop we open the popover
  // oxlint-disable-next-line no-negated-condition
  return !hideSubMenu ? (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <Button
          variant={'ghost'}
          size="small"
          data-testid="project-more-menu-button"
          iconLeft={<DotsHorizontalIcon className=" text-cf-purple-900" />}
        />
      </Popover.Trigger>
      <Popover.Content
        side="bottom"
        align="end"
        className="bg-cf-white p-1 box-shadow min-w-40 rounded-2 max-w-96 shadow-[0px_2px_6px_2px_rgba(0,0,0,0.125)]"
        sideOffset={8}
        data-testid="project-more-menu-popover"
      >
        {showViewPostLink ? (
          <div className="cursor-pointer p-2 hover:bg-cf-grey-100 rounded relative w-full">
            <Link
              target="_blank"
              href={postUrl}
              className="flex flex-row items-center gap-2 justify-start"
              data-testid="project-more-menu-view-post-link"
            >
              <IconLink className="size-4" />
              <p className="text-14 font-medium">{t('view_post')}</p>
            </Link>
          </div>
        ) : null}
        {isLoggedInQuery.data && showDelete ? (
          <button
            type="button"
            className="flex flex-row items-center cursor-pointer justify-start p-2 gap-2 hover:bg-cf-grey-100 rounded relative w-full"
            onClick={() => handleOpenDeleteDialog()}
            data-testid="project-more-menu-delete-post"
          >
            <IconDelete className="size-4" />
            <p className="text-14 font-medium">{t('delete_post')}</p>
          </button>
        ) : null}
      </Popover.Content>
    </Popover.Root>
  ) : null
}

export function ProjectMoreMenuModal({
  isOpen,
  closeModal,
  project,
  handleToggleBookmark,
  handleShareProject,
  showViewPostLink,
  showDelteOption,
}: {
  isOpen: boolean
  closeModal: () => void
  project: Project | undefined
  handleToggleBookmark: () => void
  handleShareProject: () => void
  showViewPostLink?: boolean
  showDelteOption: boolean
}) {
  const t = useTranslations('pages.masterfeed')
  const { breakpoint } = useBreakpoint()
  const isMobile = ['xs', 'sm'].includes(breakpoint ?? '')
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false)
  const { basePath } = useFeedRoute()
  const postUrl = `${process.env.NEXT_PUBLIC_CF_URL}${basePath}/${project?.postSlug}/`
  // This modal is only for mobile, the desktop version is a dropdown menu
  if (!project) {
    return null
  }

  if (!isMobile) {
    return null
  }

  const handleDeleteDialog = () => {
    setShowDeleteDialog(true)
  }

  const closeDeleteDialog = () => {
    setShowDeleteDialog(false)
    closeModal()
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={open => !open && closeDeleteDialog()}>
      <React.Suspense fallback={null}>
        <Dialog.Portal>
          <Dialog.Body
            placement={isMobile ? 'bottom' : 'middle'}
            className={tw(
              `font-poppins xl:items-center transition-transform duration-300 ease-in-out`,
              isOpen && isMobile ? 'translate-y-0' : 'translate-y-full',
            )}
            overlayZIndex={Z_INDEX_MODALS.authentication.react - 1}
            zIndex={Z_INDEX_MODALS.authentication.react}
          >
            <div className="w-full md:max-w-xl rounded-t-[20px] bg-cf-white md:rounded-[12px] py-5 px-3 relative shadow-md">
              {!showDeleteDialog ? (
                <>
                  <div className="flex justify-between items-center pt-1 pb-4">
                    <p className="text-16 font-semibold px-2">{t('submenu_title')}</p>
                    <Button
                      onClick={() => closeDeleteDialog()}
                      className="pointer-events-auto"
                      variant="ghost"
                      iconLeft={<IconClose className="!size-6" />}
                    />
                  </div>
                  <div className="flex flex-col gap-1 md:gap-3">
                    {showViewPostLink ? (
                      <Link
                        href={postUrl}
                        target="_blank"
                        className="flex flex-row items-center cursor-pointer justify-start p-2 gap-2 hover:bg-cf-grey-100 rounded relative w-full"
                      >
                        <IconLink className="size-4" />
                        <p className="text-14 font-medium">{t('view_post')}</p>
                      </Link>
                    ) : null}

                    {showDelteOption ? (
                      <button
                        type="button"
                        onClick={() => handleDeleteDialog()}
                        className="flex flex-row items-center cursor-pointer justify-start p-2 gap-2 hover:bg-cf-grey-100 rounded relative w-full"
                      >
                        <IconDelete className={'!size-4'} />
                        <p className="text-14 font-medium">{t('delete_post')}</p>
                      </button>
                    ) : null}

                    <button
                      type="button"
                      onClick={() => handleShareProject()}
                      className="flex flex-row items-center cursor-pointer justify-start p-2 gap-2 hover:bg-cf-grey-100 rounded relative w-full"
                    >
                      <IconShare className={'!size-4'} />
                      <p className="text-14 font-medium">{t('share_post')}</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleToggleBookmark()}
                      className="flex flex-row items-center cursor-pointer justify-start p-2 gap-2 hover:bg-cf-grey-100 rounded relative w-full"
                    >
                      {project.isSaved ? (
                        <IconBookmark className={'!size-4'} />
                      ) : (
                        <IconBookmarkAdd className={'!size-4'} />
                      )}
                      <p className="text-14 font-medium">
                        {project.isSaved ? t('saved_post') : t('save_post')}
                      </p>
                    </button>
                  </div>
                </>
              ) : (
                <DeleteDialogMobile targetId={project.id} closeDialog={closeDeleteDialog} />
              )}
            </div>
          </Dialog.Body>
        </Dialog.Portal>
      </React.Suspense>
    </Dialog.Root>
  )
}
